import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BudgetForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    clientType: '',
    serviceType: '',
    numberOfPalms: '',
    location: '',
    urgency: false,
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        client_type: formData.clientType,
        service_type: formData.serviceType,
        number_of_palms: formData.numberOfPalms ? parseInt(formData.numberOfPalms) : null,
        location: formData.location,
        urgency: formData.urgency,
        description: formData.description || null
      };

      await axios.post(`${API}/public/budget`, payload);
      toast.success('La seva sol·licitud de pressupost s\'ha rebut correctament. Li respondrem en les pròximes 24-48 hores.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        clientType: '',
        serviceType: '',
        numberOfPalms: '',
        location: '',
        urgency: false,
        description: ''
      });
    } catch (error) {
      toast.error('Error en enviar la sol·licitud. Si us plau, intenti-ho de nou.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-lg">
      <CardHeader>
        <CardTitle>Sol·licitar pressupost</CardTitle>
        <CardDescription>
          Proporciona'ns els detalls i t'enviarem un pressupost personalitzat en 24-48h
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget-name">Nom complet *</Label>
              <Input
                id="budget-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="El vostre nom"
                className="border-slate-300 focus:border-emerald-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget-email">Correu electrònic *</Label>
              <Input
                id="budget-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="exemple@correu.com"
                className="border-slate-300 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget-phone">Telèfon *</Label>
              <Input
                id="budget-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="600 000 000"
                className="border-slate-300 focus:border-emerald-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget-clientType">Tipus de client *</Label>
              <Select onValueChange={(value) => handleSelectChange('clientType', value)} value={formData.clientType}>
                <SelectTrigger id="budget-clientType" className="border-slate-300 focus:border-emerald-500">
                  <SelectValue placeholder="Selecciona tipus" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="particular">Particular</SelectItem>
                  <SelectItem value="empresa">Empresa</SelectItem>
                  <SelectItem value="ajuntament">Ajuntament</SelectItem>
                  <SelectItem value="comunitat">Comunitat de veïns</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget-serviceType">Servei sol·licitat *</Label>
              <Select onValueChange={(value) => handleSelectChange('serviceType', value)} value={formData.serviceType}>
                <SelectTrigger id="budget-serviceType" className="border-slate-300 focus:border-emerald-500">
                  <SelectValue placeholder="Selecciona servei" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="picudo">Tractament Picudo Rojo</SelectItem>
                  <SelectItem value="preventiu">Tractament Preventiu</SelectItem>
                  <SelectItem value="poda">Poda i Manteniment</SelectItem>
                  <SelectItem value="venda">Compra de Palmeres</SelectItem>
                  <SelectItem value="cirurgia">Cirurgia de Palmeres</SelectItem>
                  <SelectItem value="altre">Altres serveis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget-numberOfPalms">Nombre de palmeres</Label>
              <Input
                id="budget-numberOfPalms"
                name="numberOfPalms"
                type="number"
                min="1"
                value={formData.numberOfPalms}
                onChange={handleChange}
                placeholder="Ex: 3"
                className="border-slate-300 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="budget-location">Localització *</Label>
            <Input
              id="budget-location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              placeholder="Ciutat o municipi"
              className="border-slate-300 focus:border-emerald-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="budget-description">Descripció detallada</Label>
            <Textarea
              id="budget-description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Proporciona'ns més detalls sobre el servei que necessites..."
              rows={4}
              className="border-slate-300 focus:border-emerald-500 resize-none"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="budget-urgency"
              name="urgency"
              checked={formData.urgency}
              onCheckedChange={(checked) => handleSelectChange('urgency', checked)}
            />
            <Label
              htmlFor="budget-urgency"
              className="text-sm font-normal cursor-pointer"
            >
              És un cas urgent (palmera greument afectada)
            </Label>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviant...
              </>
            ) : (
              'Sol·licitar pressupost gratuït'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default BudgetForm;
